/**
 * Convert HTML content into plain text while preserving paragraph breaks.
 *
 * @param {string} html - Raw HTML string to convert into plain text.
 * @returns {string} Plain text representation with normalized whitespace.
 */
export function htmlToPlainText(html = '') {
  if (!html) return '';

  const doc = new DOMParser().parseFromString(html, 'text/html');
  doc
    .querySelectorAll(
      '.ql-cursor, script, style, template, iframe, object, embed'
    )
    .forEach((el) => el.remove());

  const blocks = [];

  const normalizeText = (value) =>
    value
      .replace(/[\uFEFF\u200B\u200C\u200D]/g, '')
      .replace(/\u00A0/g, ' ')
      .replace(/[ \t]+\n/g, '\n')
      .replace(/\n[ \t]+/g, '\n')
      .replace(/[ \t]{2,}/g, ' ');

  const textFromNode = (node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      return node.nodeValue || '';
    }
    if (node.nodeType !== Node.ELEMENT_NODE) {
      return '';
    }
    const tagName = node.tagName.toUpperCase();
    if (tagName === 'BR') {
      return '\n';
    }
    let result = '';
    node.childNodes.forEach((child) => {
      result += textFromNode(child);
    });
    return result;
  };

  const collectBlocks = (node) => {
    node.childNodes.forEach((child) => {
      if (child.nodeType === Node.TEXT_NODE) {
        const text = normalizeText(child.nodeValue || '').trim();
        if (text) {
          blocks.push(text);
        }
        return;
      }
      if (child.nodeType !== Node.ELEMENT_NODE) return;

      const tagName = child.tagName.toUpperCase();
      if (tagName === 'P' || tagName === 'LI') {
        let text = normalizeText(textFromNode(child)).trim();
        if (text) {
          if (tagName === 'LI') {
            text = `- ${text}`;
          }
          blocks.push(text);
        }
        return;
      }

      if (tagName === 'DIV') {
        const hasBlockChild = child.querySelector('p, li, div');
        if (hasBlockChild) {
          collectBlocks(child);
          return;
        }
        const text = normalizeText(textFromNode(child)).trim();
        if (text) {
          blocks.push(text);
        }
        return;
      }

      collectBlocks(child);
    });
  };

  collectBlocks(doc.body);

  const output = blocks.join('\n\n');
  return normalizeText(output)
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}
